/**
 * Small, dependency-free geographic helpers used by the itinerary and
 * map components. Nothing here reaches for `leaflet` so that pure logic
 * stays testable without a DOM.
 */

export interface LatLng {
  lat: number;
  lng: number;
}

const EARTH_RADIUS_KM = 6371;
const KM_TO_MI = 0.621371;
const toRad = (deg: number): number => (deg * Math.PI) / 180;

/**
 * Great-circle distance between two points, in kilometres.
 *
 * Uses the haversine formula, accurate to within a few metres at
 * road-trip scales.
 */
export function haversineKm(a: LatLng, b: LatLng): number {
  const dLat = toRad(b.lat - a.lat);
  const dLng = toRad(b.lng - a.lng);
  const lat1 = toRad(a.lat);
  const lat2 = toRad(b.lat);

  const h =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) ** 2;
  return 2 * EARTH_RADIUS_KM * Math.asin(Math.sqrt(h));
}

/** Great-circle distance in miles. */
export const haversineMi = (a: LatLng, b: LatLng): number =>
  haversineKm(a, b) * KM_TO_MI;

/**
 * Total route length in miles following the given ordered points.
 *
 * This is a straight-line ("as the crow flies") approximation, which is
 * intentionally conservative — real driving distance is typically
 * 1.25–1.5× this value in Vermont / Maine road networks.
 */
export function routeDistanceMi(points: LatLng[]): number {
  if (points.length < 2) return 0;
  let total = 0;
  for (let i = 1; i < points.length; i++) {
    total += haversineMi(points[i - 1], points[i]);
  }
  return total;
}

/**
 * Minimum / maximum lat/lng box enclosing a set of points.
 * Returns `null` for empty input.
 */
export function bounds(points: LatLng[]): {
  min: LatLng;
  max: LatLng;
} | null {
  if (!points.length) return null;
  let minLat = points[0].lat;
  let maxLat = points[0].lat;
  let minLng = points[0].lng;
  let maxLng = points[0].lng;
  for (const p of points) {
    if (p.lat < minLat) minLat = p.lat;
    if (p.lat > maxLat) maxLat = p.lat;
    if (p.lng < minLng) minLng = p.lng;
    if (p.lng > maxLng) maxLng = p.lng;
  }
  return { min: { lat: minLat, lng: minLng }, max: { lat: maxLat, lng: maxLng } };
}
